/**
 * @engine/input — Multi-Tap Interaction
 *
 * Triggers after multiple quick taps.
 */

import type { InteractionType, ControlValue, ActionPhase, ActionContext, MultiTapParams } from '../types';
import { InputInteraction, InteractionState, registerInteraction } from './InputInteraction';

interface MultiTapState extends InteractionState {
  wasActive: boolean;
  tapCount: number;
  lastTapTime: number;
  tapStartTime: number;
}

/**
 * Multi-tap interaction — triggers after N quick taps
 */
export class MultiTapInteraction extends InputInteraction {
  readonly type: InteractionType = 'multiTap';

  /** Number of taps required */
  private readonly tapCount: number;

  /** Max time between taps (seconds) */
  private readonly tapDelay: number;

  /** Max duration for each tap (seconds) */
  private readonly tapDuration: number;

  constructor(params: Partial<MultiTapParams> = {}) {
    super(params as Record<string, unknown>);
    this.tapCount = params.tapCount ?? 2;
    this.tapDelay = params.tapDelay ?? 0.2;
    this.tapDuration = params.tapDuration ?? 0.2;
  }

  override createState(): MultiTapState {
    return {
      phase: 'waiting',
      startTime: 0,
      wasActive: false,
      tapCount: 0,
      lastTapTime: 0,
      tapStartTime: 0,
    };
  }

  process(
    state: InteractionState,
    value: ControlValue,
    time: number,
    _context: ActionContext
  ): ActionPhase {
    const tapState = state as MultiTapState;
    const isActive = this.isActive(value);
    const wasActive = tapState.wasActive;
    tapState.wasActive = isActive;

    // Check if we've timed out waiting for next tap
    if (tapState.tapCount > 0 && time - tapState.lastTapTime > this.tapDelay) {
      // Reset tap count
      tapState.tapCount = 0;
      if (state.phase === 'started') {
        state.phase = 'canceled';
        return 'canceled';
      }
    }

    // Just pressed
    if (isActive && !wasActive) {
      tapState.tapStartTime = time;

      // Check if this is a continuation of a multi-tap sequence
      if (tapState.tapCount > 0 && time - tapState.lastTapTime <= this.tapDelay) {
        // Continuing sequence
        if (state.phase !== 'started') {
          state.phase = 'started';
          state.startTime = time;
        }
      } else {
        // New sequence
        tapState.tapCount = 0;
        state.phase = 'started';
        state.startTime = time;
      }

      return 'started';
    }

    // Just released (potential tap)
    if (!isActive && wasActive) {
      const tapDuration = time - tapState.tapStartTime;

      // Check if this was a valid tap (short enough)
      if (tapDuration <= this.tapDuration) {
        tapState.tapCount++;
        tapState.lastTapTime = time;

        // Check if we've reached the required tap count
        if (tapState.tapCount >= this.tapCount) {
          state.phase = 'performed';
          tapState.tapCount = 0; // Reset for next sequence
          return 'performed';
        }
      } else {
        // Tap was too long, reset
        tapState.tapCount = 0;
        state.phase = 'canceled';
        return 'canceled';
      }

      return 'started';
    }

    // Holding too long
    if (isActive && wasActive) {
      const holdDuration = time - tapState.tapStartTime;
      if (holdDuration > this.tapDuration && tapState.tapCount > 0) {
        // Holding too long, cancel
        tapState.tapCount = 0;
        state.phase = 'canceled';
        return 'canceled';
      }
    }

    return state.phase === 'performed' ? 'waiting' : state.phase;
  }

  override reset(state: InteractionState): void {
    super.reset(state);
    const tapState = state as MultiTapState;
    tapState.wasActive = false;
    tapState.tapCount = 0;
    tapState.lastTapTime = 0;
    tapState.tapStartTime = 0;
  }
}

/**
 * Tap interaction (single tap) — shorthand for MultiTap with count=1
 * Accepts `maxDuration` as alias for `tapDuration` for convenience.
 */
export class TapInteraction extends MultiTapInteraction {
  override readonly type: InteractionType = 'tap';

  constructor(params: Record<string, unknown> = {}) {
    // Accept maxDuration as alias for tapDuration
    const tapDuration = (params.maxDuration ?? params.tapDuration) as number | undefined;
    super({ ...params, tapCount: 1, tapDuration });
  }
}

/**
 * Slow tap interaction — longer tap duration allowed
 */
export class SlowTapInteraction extends InputInteraction {
  readonly type: InteractionType = 'slowTap';

  private readonly minDuration: number;
  private readonly maxDuration: number;

  constructor(params: Record<string, unknown> = {}) {
    super(params);
    this.minDuration = (params.minDuration as number) ?? 0.2;
    this.maxDuration = (params.maxDuration as number) ?? 0.5;
  }

  override createState(): InteractionState & { wasActive: boolean; tapStartTime: number } {
    return {
      phase: 'waiting',
      startTime: 0,
      wasActive: false,
      tapStartTime: 0,
    };
  }

  process(
    state: InteractionState,
    value: ControlValue,
    time: number,
    _context: ActionContext
  ): ActionPhase {
    const s = state as InteractionState & { wasActive: boolean; tapStartTime: number };
    const isActive = this.isActive(value);
    const wasActive = s.wasActive;
    s.wasActive = isActive;

    if (isActive && !wasActive) {
      s.tapStartTime = time;
      state.phase = 'started';
      state.startTime = time;
      return 'started';
    }

    if (!isActive && wasActive) {
      const duration = time - s.tapStartTime;
      if (duration >= this.minDuration && duration <= this.maxDuration) {
        state.phase = 'performed';
        return 'performed';
      }
      state.phase = 'canceled';
      return 'canceled';
    }

    if (isActive && wasActive) {
      const duration = time - s.tapStartTime;
      if (duration > this.maxDuration) {
        state.phase = 'canceled';
        return 'canceled';
      }
    }

    return state.phase === 'performed' ? 'waiting' : state.phase;
  }
}

registerInteraction('multiTap', MultiTapInteraction);
registerInteraction('tap', TapInteraction);
registerInteraction('slowTap', SlowTapInteraction);
